/**
 * API Preview Service
 *
 * Handles generation of API URL and cURL command previews
 */

import { buildExportApiRequestString, buildExportCurlRequestString } from './ExportRequestService';

import type {
  PreviewGenerationContext,
  PreviewResult,
  PreviewService
} from './PreviewService.types';

export class APIPreviewService implements PreviewService {
  /**
   * Generate API URL string
   */
  generateAPIUrl(context: PreviewGenerationContext): string {
    const { rangeStart, rangeEnd, includeMetadata, timezone, symbols } = context;

    return buildExportApiRequestString({
      symbols,
      fromValue: rangeStart,
      toValue: rangeEnd || null,
      includeMetadata,
      userTimezone: timezone
    });
  }

  /**
   * Generate cURL command string
   */
  generateCurlCommand(context: PreviewGenerationContext): string {
    const { rangeStart, rangeEnd, includeMetadata, timezone, apiKey, symbols } = context;

    return buildExportCurlRequestString({
      symbols,
      fromValue: rangeStart,
      toValue: rangeEnd || null,
      includeMetadata,
      userTimezone: timezone,
      apiKey
    });
  }

  /**
   * Generate preview (defaults to API URL)
   */
  generatePreview(context: PreviewGenerationContext): PreviewResult {
    try {
      const content = this.generateAPIUrl(context);
      return { content };
    } catch (error) {
      return {
        content: '',
        error: error instanceof Error ? error.message : 'Failed to generate API preview'
      };
    }
  }

  /**
   * Generate cURL preview
   */
  generateCurlPreview(context: PreviewGenerationContext): PreviewResult {
    try {
      const content = this.generateCurlCommand(context);
      return { content };
    } catch (error) {
      return {
        content: '',
        error: error instanceof Error ? error.message : 'Failed to generate cURL preview'
      };
    }
  }
}

// Export singleton instance
export const apiPreviewService = new APIPreviewService();
